import { serverDateSort } from 'components/Tables/dateSorting';
import React, { useCallback, useContext, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  QUERY_GET_USER,
  QUERY_LIST_USERS,
  MUTATION_UPDATE_USER,
  MUTATION_UPDATE_NOTIFICATION_PREFERENCES,
  QUERY_GET_NOTIFICATION_PREFERENCES,
} from '../../../queries/UserQueries';
import UserForm from './components/UserForm';
import routes from 'constants/routes';
import { Tabs, Button, Col, Popconfirm, Row, message } from 'antd';
import {
  Link,
  useSearchParams,
  useNavigate,
  useParams,
} from 'react-router-dom';
import useTable from 'components/CCCTable/useTable';
import CCCTable from 'components/CCCTable/CCCTable';
import {
  MUTATION_UPDATE_LOCATION_ROLE,
  QUERY_LIST_LOCATION_ROLES,
  QUERY_TOTAL_LOCATION_ROLES,
} from '../Locations/queries';
import dayjs from 'dayjs';
import { FULL_DATE } from '../../../constants/dateformats';
import stripTypename from '../../../services/stripTypename';
import { UserContext } from '../../../contexts/UserContext';
import AssignmentTable from '../../../components/Tables/AssignmentTable';
import { FIELDS_NAMES, TAB_TABLE_SCROLL } from 'components/Tables/const';
import UserPreferencesForm from './components/UserPreferencesForm';
import DangerZone from './components/DangerZone';
import UserFeeLedgerTable from '../../../components/Tables/UserFeeLedgerTable';
import FeeTransactionsTable from '../../../components/Tables/FeeTransactionsTable';
import PermissionsAlert from '../../../components/PermissionsAlert/PermissionsAlert';
import Loader from '../../../components/Loader/Loader';
import { QUERY_USER_FEE_TRANSACTIONS } from '../../../queries/FeeTransactionsQueries';
import { prepareUserUpdateInput } from '../../../utils/user';

const UsersEdit = () => {
  const navigate = useNavigate();
  const { user_id } = useParams();
  const { dbUser } = useContext(UserContext);
  const { setTableRef, refetch } = useTable();
  const [messageApi, contextHolder] = message.useMessage();
  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamsTabKey = String(searchParams.get('tab'));

  const {
    data,
    loading,
    error,
    refetch: refetchUser,
  } = useQuery(QUERY_GET_USER, {
    variables: { user_id },
  });

  const { data: feeTransactionsData, error: feeTransactionsError } = useQuery(
    QUERY_USER_FEE_TRANSACTIONS,
    {
      variables: {
        user_id,
      },
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-first',
    },
  );

  const {
    data: dataNotificationPreferences,
    loading: loadingNotificationPreferences,
    error: errorNotificationPreferences,
  } = useQuery(QUERY_GET_NOTIFICATION_PREFERENCES, {
    variables: { user_id },
  });
  const [updateUser] = useMutation(MUTATION_UPDATE_USER);
  const [updateLocationRole] = useMutation(MUTATION_UPDATE_LOCATION_ROLE);
  const [updateNotificationPreferences] = useMutation(
    MUTATION_UPDATE_NOTIFICATION_PREFERENCES,
  );
  const submit = useCallback(
    async (values: any) => {
      const input = prepareUserUpdateInput(values);

      const response = await updateUser({
        variables: {
          user_id,
          input,
        },
        refetchQueries: [
          {
            query: QUERY_GET_USER,
            variables: {
              user_id,
            },
          },
          {
            query: QUERY_LIST_USERS,
          },
        ],
      });
      const responseData = response?.data?.updateUser;
      if (responseData?.success) {
        messageApi.success(responseData?.error_message?.message);
        setTimeout(() => {
          navigate(routes.USERS);
        }, 1300);
      } else {
        messageApi.error(responseData?.error_message?.message);
        setTimeout(() => {
          navigate(routes.USERS);
        }, 3000);
      }
    },
    [navigate, updateUser, user_id],
  );
  const submitNotificationPreferences = useCallback(
    async (prefs: string) => {
      await updateNotificationPreferences({
        variables: {
          user_id,
          input: {
            prefs,
          },
        },
      });
    },
    [navigate, user_id],
  );

  const locationRoleColumns: any[] = useMemo(
    () => [
      {
        title: 'Location',
        dataIndex: ['location', 'name'],
        render: (loc_name: string) => {
          return loc_name || '- All -';
        },
      },
      {
        title: 'Full Name',
        dataIndex: ['user', 'full_name'],
      },
      {
        title: 'Role',
        dataIndex: ['role', 'name'],
      },
      {
        title: 'Assigned By',
        dataIndex: ['assigned_by', 'full_name'],
      },
      {
        title: 'Assigned On',
        ...serverDateSort(true),
        dataIndex: 'assigned_on',
        render: (assigned_on: string) =>
          assigned_on ? dayjs(assigned_on).format(FULL_DATE) : null,
      },

      {
        title: 'Disabled On',
        ...serverDateSort(),
        dataIndex: 'disabled_on',
        render: (disabled_on: string) =>
          disabled_on ? dayjs(disabled_on).format(FULL_DATE) : null,
        width: 120,
      },
      ...(dbUser?.userPermissions.ManageLocations
        ? [
            {
              title: 'Actions',
              key: 'actions',
              render: (record: any) => (
                <Popconfirm
                  title="Are you sure?"
                  onConfirm={async () => {
                    const input: any = {};
                    for (const key in record) {
                      if (typeof record[key] !== 'object') {
                        input[key] = record[key];
                      }
                    }
                    delete input.location_role_id;

                    await updateLocationRole({
                      variables: {
                        location_role_id: record.location_role_id,
                        input: {
                          ...stripTypename(input),
                          ...(!record.disabled_on
                            ? {
                                disabled_on: new Date(),
                                disabled_by_id: dbUser ? dbUser.user_id : null,
                              }
                            : {
                                disabled_on: null,
                                disabled_by_id: null,
                              }),
                        },
                      },
                    });
                    refetch();
                  }}
                >
                  <a>{record.disabled_on ? 'Activate' : 'Disable'}</a>
                </Popconfirm>
              ),
            },
          ]
        : []),
    ],
    [dbUser],
  );

  const handleTabsChange = useCallback((key: string) => {
    setSearchParams({ tab: key });
  }, []);

  const tabItems = [
    {
      key: '1',
      label: 'Location Roles',
      children: (
        <CCCTable
          ref={setTableRef}
          rowKey="location_role_id"
          query={QUERY_LIST_LOCATION_ROLES}
          queryKey="listLocationRole"
          totalQuery={QUERY_TOTAL_LOCATION_ROLES}
          totalQueryKey="totalLocationRole"
          columns={locationRoleColumns}
          where={{ user_id: { eq: user_id } }}
          scroll={TAB_TABLE_SCROLL}
        />
      ),
    },
    {
      key: '2',
      label: "User's Assignments",
      children: searchParamsTabKey === '2' && (
        <AssignmentTable
          where={{ user_id: { eq: user_id } }}
          hiddenColumns={[FIELDS_NAMES.USER]}
          scroll={TAB_TABLE_SCROLL}
        />
      ),
    },
    {
      key: '3',
      label: 'Notifications Preferences',
      children:
        !loadingNotificationPreferences && !errorNotificationPreferences ? (
          <UserPreferencesForm
            data={dataNotificationPreferences.getNotificationPreferences}
            handleSubmit={submitNotificationPreferences}
          />
        ) : errorNotificationPreferences ? (
          `Error: ${errorNotificationPreferences.message}`
        ) : (
          'loading...'
        ),
    },
    {
      key: '4',
      label: 'Fee Ledger',
      children: user_id ? (
        <UserFeeLedgerTable
          key={user_id}
          userId={user_id}
          active={searchParamsTabKey === '4'}
          scroll={TAB_TABLE_SCROLL}
        />
      ) : null,
    },
    {
      key: '5',
      label: 'Transactions',
      children:
        feeTransactionsData && !feeTransactionsError ? (
          <FeeTransactionsTable
            data={feeTransactionsData?.getFeeTransactions}
            scroll={TAB_TABLE_SCROLL}
          />
        ) : null,
    },
  ];

  const activeTabKey = tabItems.map((it) => it.key).includes(searchParamsTabKey)
    ? searchParamsTabKey
    : '1';

  if (loading) {
    return <Loader />;
  }

  if (!dbUser?.userPermissions.ManageUsers) {
    return <PermissionsAlert />;
  }

  return (
    <>
      <Row style={{ paddingLeft: 18 }}>
        <Row align={'middle'} justify={'space-between'}>
          <Col span={12}>
            {!loading && !error ? (
              <UserForm
                user={data.getUser}
                handleSubmit={submit}
                refetchUser={refetchUser}
              />
            ) : error ? (
              'Error:' + error.message
            ) : (
              'loading...'
            )}
          </Col>
          {dbUser?.userPermissions.DangerZone &&
            data?.getUser.firebase_user_id &&
            !data?.getUser.email?.includes('DELETED') && (
              <Col span={11}>
                <DangerZone user={data?.getUser} />
              </Col>
            )}
        </Row>
        <Col span={24} style={{ marginTop: 50 }}>
          <Tabs
            items={tabItems}
            activeKey={activeTabKey}
            onChange={handleTabsChange}
            tabBarExtraContent={
              dbUser?.userPermissions.ManageLocations
                ? {
                    right: (
                      <Link
                        to={'/locations/role/create'}
                        state={{
                          user: data ? data.getUser : { user_id },
                          backTo: routes.USERS + '/' + user_id,
                        }}
                      >
                        <Button type="primary">Create Location Role</Button>
                      </Link>
                    ),
                  }
                : null
            }
          ></Tabs>
        </Col>
      </Row>
      {contextHolder}
    </>
  );
};

export default UsersEdit;
